import { useScrollAnimationSection } from '../../../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';
import { articles } from '../../../mocks/articles';

export default function ContentGuide() {
  const sectionRef = useScrollAnimationSection();
  const navigate = useNavigate();

  const getFirstArticleByStep = (step: string) => {
    const found = articles.find((a) => a.step === step);
    return found ? found.id : null;
  };

  const handleCardClick = (step: string) => {
    const id = getFirstArticleByStep(step);
    if (id) navigate(`/articles/${id}`);
  };

  const steps = [
    { number: '01', label: '知識をつける', desc: '家づくりの基礎を理解する' },
    { number: '02', label: '予算を決める', desc: '資金計画・ローンを把握する' },
    { number: '03', label: 'パートナーを選ぶ', desc: 'HM・工務店を比較検討する' },
    { number: '04', label: '物件・プランを決める', desc: '土地選びと間取りを考える' },
    { number: '05', label: '契約・引き渡し', desc: '安心して最終ステップへ' },
  ];

  const stepCards = [
    {
      step: '01',
      label: '知識をつける',
      icon: 'ri-book-open-line',
      title: '家づくりの基本を\nゼロからわかりやすく解説',
      desc: '流れ・用語・失敗しないための心得まで。これを読めば全体像が見えてきます。',
      tagBg: 'bg-[#8FA882]',
      tagText: 'text-white',
      image: 'https://public.readdy.ai/ai/img_res/ee37e200dc2deb92a62fa90b74db6b7a.jpg',
      size: 'big',
    },
    {
      step: '02',
      label: '予算を決める',
      icon: 'ri-money-yen-circle-line',
      title: '頭金・諸費用・住宅ローン\nお金の全体像を把握しよう',
      desc: '無理のない資金計画の立て方を具体的な数字で解説します。',
      tagBg: 'bg-[#C8A870]',
      tagText: 'text-white',
      image: 'https://readdy.ai/api/search-image?query=A%20clean%20Nordic%20style%20desk%20flat%20lay%20with%20house%20model%20figurine%2C%20financial%20planning%20notebook%20open%20to%20budget%20pages%2C%20calculator%2C%20pen%20and%20coins%2C%20soft%20morning%20natural%20light%2C%20warm%20beige%20and%20cream%20tones%2C%20no%20people%2C%20minimal%20Japanese%20lifestyle%20photography%2C%20top%20down%20view%2C%20organized%20and%20simple&width=600&height=280&seq=step02&orientation=landscape',
      size: 'small',
    },
    {
      step: '03',
      label: 'パートナーを選ぶ',
      icon: 'ri-building-4-line',
      title: 'ハウスメーカー・工務店\n後悔しない選び方のポイント',
      desc: '比較のコツ・見学時のチェックポイント・営業トークへの対処法。',
      tagBg: 'bg-[#7A9A8A]',
      tagText: 'text-white',
      image: 'https://readdy.ai/api/search-image?query=A%20welcoming%20modern%20Japanese%20house%20builder%20showroom%20exterior%20with%20clean%20white%20facade%2C%20wood%20accents%2C%20green%20landscaping%2C%20golden%20afternoon%20sunlight%2C%20Nordic%20minimal%20architecture%2C%20professional%20real%20estate%20style%20photography%2C%20warm%20and%20trustworthy%20atmosphere%2C%20peaceful%20suburban%20setting&width=600&height=280&seq=step03&orientation=landscape',
      size: 'small',
    },
    {
      step: '04',
      label: '物件・プランを決める',
      icon: 'ri-map-pin-2-line',
      title: '土地選び・間取り・エリア検討\n自分にぴったりの住まいを見つける',
      desc: '立地条件の見方・間取りの考え方・内見時に確認すべきことを詳しく紹介。',
      tagBg: 'bg-[#9A8A7A]',
      tagText: 'text-white',
      image: 'https://readdy.ai/api/search-image?query=A%20beautiful%20modern%20Japanese%20suburban%20house%20exterior%20with%20elegant%20garden%2C%20stone%20pathway%2C%20lush%20green%20trees%2C%20warm%20golden%20hour%20sunlight%2C%20Nordic%20minimal%20architecture%2C%20professional%20real%20estate%20photography%2C%20inviting%20and%20residential%2C%20clean%20white%20walls%20with%20wood%20accents%2C%20peaceful%20neighborhood&width=600&height=300&seq=step04&orientation=landscape',
      size: 'wide',
    },
    {
      step: '05',
      label: '契約・引き渡し',
      icon: 'ri-file-list-3-line',
      title: '売買契約・登記・引き渡し\n最終ステップを安心してクリア',
      desc: '重要事項説明・契約書のチェックポイント・引き渡しまでの流れをわかりやすく解説。',
      tagBg: 'bg-[#6A7A8A]',
      tagText: 'text-white',
      image: 'https://readdy.ai/api/search-image?query=A%20cozy%20and%20warm%20Japanese%20home%20entryway%20with%20new%20house%20keys%20on%20wooden%20table%2C%20small%20house%20plant%2C%20soft%20natural%20light%2C%20minimalist%20Nordic%20interior%20design%2C%20milestone%20and%20celebration%20atmosphere%2C%20neutral%20tones%20with%20warm%20wood%20accents%2C%20no%20people%2C%20clean%20and%20inviting%2C%20lifestyle%20photography&width=600&height=300&seq=step05&orientation=landscape',
      size: 'wide',
    },
  ];

  const categoryTags = [
    { id: 1, step: '01', label: '知識をつける', icon: 'ri-book-open-line', count: 14 },
    { id: 2, step: '02', label: '予算を決める', icon: 'ri-money-yen-circle-line', count: 18 },
    { id: 3, step: '03', label: 'パートナーを選ぶ', icon: 'ri-building-4-line', count: 11 },
    { id: 4, step: '04', label: '物件・プランを決める', icon: 'ri-map-pin-2-line', count: 16 },
    { id: 5, step: '05', label: '契約・引き渡し', icon: 'ri-file-list-3-line', count: 9 },
  ];

  const bigCard = stepCards[0];
  const smallCards = stepCards.slice(1, 3);
  const wideCards = stepCards.slice(3, 5);

  return (
    <section
      id="content"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FAFAF8]"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8F0E6] text-[#6A9060] text-xs font-semibold tracking-wide mb-4">
            <i className="ri-map-2-line" />
            ステップで学ぶ
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] leading-tight">
            家づくりのステップを、<br />
            <span className="text-[#8FA882]">やさしく解説</span>
          </h2>
          <p className="mt-4 text-[#6B6B6B] text-base leading-relaxed max-w-md mx-auto">
            複雑に見える住宅購入も、順番に理解すれば大丈夫。<br />
            初心者でも安心して読み進められます。
          </p>
        </div>

        {/* Step Progress */}
        <div className="fade-in-up flex items-center justify-center gap-0 mb-16 overflow-x-auto pb-2">
          {steps.map((step, i) => (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center min-w-[110px]">
                <div className="w-12 h-12 rounded-full bg-[#8FA882] flex items-center justify-center text-white font-bold text-sm">
                  {step.number}
                </div>
                <p className="text-[#2C2C2C] text-xs font-semibold mt-2 whitespace-nowrap text-center">{step.label}</p>
                <p className="text-[#6B6B6B] text-xs mt-0.5 whitespace-nowrap text-center">{step.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="w-8 md:w-12 h-px bg-[#C8D8C0] mx-1 flex-shrink-0 mt-[-16px]" />
              )}
            </div>
          ))}
        </div>

        {/* Cards Grid — Row 1: Big + 2 Stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          {/* Big card — Step 01 */}
          <div
            className="fade-in-up group relative rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-300"
            onClick={() => handleCardClick(bigCard.step)}
          >
            <div className="w-full h-[430px]">
              <img
                src={bigCard.image}
                alt={bigCard.label}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/22 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${bigCard.tagBg} ${bigCard.tagText} text-xs font-semibold mb-3`}>
                <i className={bigCard.icon} />
                STEP {bigCard.step}｜{bigCard.label}
              </div>
              <h3 className="text-white text-xl font-bold leading-snug mb-2 whitespace-pre-line">
                {bigCard.title}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-4">{bigCard.desc}</p>
              <div className="flex items-center gap-1 text-[#C8D8C0] text-sm font-medium">
                <span>記事を読む</span>
                <i className="ri-arrow-right-line" />
              </div>
            </div>
          </div>

          {/* 2 stacked small cards — Step 02 & 03 */}
          <div className="flex flex-col gap-5">
            {smallCards.map((card) => (
              <div
                key={card.step}
                onClick={() => handleCardClick(card.step)}
                className="fade-in-up group relative rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-300 flex-1"
              >
                <div className="w-full h-[200px]">
                  <img
                    src={card.image}
                    alt={card.label}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${card.tagBg} ${card.tagText} text-xs font-semibold mb-2`}>
                    <i className={card.icon} />
                    STEP {card.step}｜{card.label}
                  </div>
                  <h3 className="text-white text-base font-bold leading-snug mb-1">{card.title.replace('\n', ' ')}</h3>
                  <div className="flex items-center gap-1 text-[#C8D8C0] text-xs font-medium mt-2">
                    <span>記事を読む</span>
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cards Grid — Row 2: 2 Wide cards — Step 04 & 05 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {wideCards.map((card) => (
            <div
              key={card.step}
              onClick={() => handleCardClick(card.step)}
              className="fade-in-up group relative rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="w-full h-[240px]">
                <img
                  src={card.image}
                  alt={card.label}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${card.tagBg} ${card.tagText} text-xs font-semibold mb-2`}>
                  <i className={card.icon} />
                  STEP {card.step}｜{card.label}
                </div>
                <h3 className="text-white text-base font-bold leading-snug mb-1">{card.title.replace('\n', ' ')}</h3>
                <p className="text-white/75 text-xs leading-relaxed mb-3 hidden md:block">{card.desc}</p>
                <div className="flex items-center gap-1 text-[#C8D8C0] text-sm font-medium">
                  <span>記事を読む</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </div>
          ))}
        </div>


      </div>
    </section>
  );
}
